import { forwardRef } from "react";
import { ReactNode, useState } from "react";
import textStyles from "./styles/DropdownItemTextAtom.module.css";
import checkboxStyles from "./styles/DropdownItemCheckboxAtom.module.css";
import { useFocusHandling } from "../../../hooks/useFocusHandling";
import { DropdownItemType } from "../container";
import {
  LabelPairedSquareCheckSmFillIcon,
  LabelPairedSquareSmBoldIcon,
} from "@deriv/quill-icons";

const dropdownItemBaseStyles = {
  text: textStyles,
  checkbox: checkboxStyles,
} as const;

export type DropdownItemAtomType = {
  className?: string;
  label?: string;
  showFocusRing?: boolean;

  /** Variant props */
  selected?: boolean;
  size?: "sm" | "md";
  state?: "default" | "disabled";
  textAlignment?: "left" | "center";
  width?: "fill" | "fit-content";

  /** Custom props */
  type?: "text" | "checkbox";
  onClick?: (e: DropdownItemType) => void;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  value?: string;
  itemKey?: number;
  noFocus?: boolean;
  ref?: React.Ref<HTMLDivElement>;
};

export const DropdownItemAtom = forwardRef<
  HTMLDivElement,
  DropdownItemAtomType
>(
  (
    {
      className = "",
      selected = false,
      size = "sm",
      state = "default",
      width = "fill",
      textAlignment = "left",
      label = "Item",
      showFocusRing = false,
      type = "text",
      iconLeft,
      iconRight,
      itemKey = 0,
      onClick,
      value = "",
      noFocus = false,
    },
    ref
  ) => {
    const styles = dropdownItemBaseStyles[type];
    const itemId = `quill-atom-dropdown-item-${type}-${state}-${size}-selected-${selected}`;
    const [highlight, setHighlight] = useState(showFocusRing);

    const handleClick = () => {
      if (onClick) {
        if (type === "text" && selected) {
          onClick({ label: "", value: "" });
          return false;
        }

        onClick({ label, value, iconLeft, iconRight });
      }
    };

    const { handleFocus, handleBlur } = useFocusHandling({
      isDisabled: state === "disabled",
      setHighlight,
      onEnter: handleClick,
    });

    const checkBoxIcon = selected ? (
      <LabelPairedSquareCheckSmFillIcon />
    ) : (
      <LabelPairedSquareSmBoldIcon />
    );

    return (
      <div
        ref={ref}
        className={[
          styles.root,
          className,
          "quill-ui-next-atom",
          "quill-ui-next-no-select",
          state !== "disabled" && "quill-ui-next-pointer",
        ].join(" ")}
        data-width={width}
        data-value={value}
        data-select={selected}
        data-id={itemId}
        data-size={size}
        data-state={state}
        data-text-alignment={textAlignment}
        data-item-key={itemKey}
        data-item-label={label}
        data-item-value={value}
        tabIndex={state !== "disabled" && !noFocus ? 0 : -1}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleClick}
      >
        {type === "text" && iconLeft && (
          <div className={styles.containerIconLeft}>{iconLeft}</div>
        )}

        {type === "checkbox" && checkBoxIcon && (
          <div className={styles.iconCheck}>{checkBoxIcon}</div>
        )}

        <div className={styles.item}>{label}</div>

        {iconRight && (
          <div className={styles.containerIconRight}>{iconRight}</div>
        )}
        {highlight && <div className={styles.bgFocus} />}
      </div>
    );
  }
);

export default DropdownItemAtom;
